import { useFlags } from "launchdarkly-react-client-sdk";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import { blue } from "@mui/material/colors";

import { createSelector } from "@reduxjs/toolkit";
import { skipToken } from "@reduxjs/toolkit/query";
import { useDebounce } from "hooks";
import { every, includes, isEqual } from "lodash";

import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  CLINIC_STAFF_SCHEDULE,
  SCHEDULING_V2_FEATURE
} from "constants/feature-flags";
import { VIEW_OWN_SCHEDULED_ONLY } from "constants/permissions";
import { GROUP_AUDIOLOGISTS } from "constants/staff";
import {
  selectCurrentClinic,
  selectCurrentPractice,
  selectCurrentRegion,
  selectCurrentRegionClinicsIds,
  selectCurrentUser,
  selectHasRegions,
  selectUserClinicsIds
} from "features/auth/auth-slice";
import {
  applyStaffFilter,
  selectClinicsFilterValues,
  selectDateRangeValues,
  selectStaffFilterIsReady,
  staffFilterReady
} from "features/scheduling/scheduler-slice";
import { getStaffColor } from "features/scheduling/utils/scheduling.utils";
import useUserRoles from "hooks/use-user-roles.hook";
import { useGetDefaultStaffQuery } from "services/clinic-staff-schedule";
import { useLazyGetStaffQuery } from "services/staff";
import { GroupType, StaffMember } from "services/types/staff.type";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import useStorage from "../use-storage.hook";
import { SelectedStaffMember } from "./staff-filter.component";

type StaffFilterProviderProps = {
  children?: React.ReactNode;
  disableStorage?: boolean;
};

type Ctx = {
  staff: StaffMember[];
  selectedStaff: Map<number, SelectedStaffMember>;
  handleChangeStaff: (value: Map<number, SelectedStaffMember>) => void;
  showInactiveStaff: boolean;
  setShowInactiveStaff: (v: boolean) => void;
  showAudiologistsOnly: boolean;
  setShowAudiologistsOnly: (v: boolean) => void;
  hasClinicDefaults: boolean;
  clinicDefaultsName: string | null;
  clinicDefaultsTooltip: string | null;
  resetToDefaults: () => void;
};

const StaffFilterContext = createContext<Ctx | null>(null);

// ---------- Reselect ----------
const selectStaffFilterData = createSelector(
  [
    selectCurrentClinic,
    selectCurrentPractice,
    selectHasRegions,
    selectCurrentRegion,
    selectClinicsFilterValues,
    selectCurrentUser,
    selectStaffFilterIsReady,
    selectCurrentRegionClinicsIds,
    selectUserClinicsIds
  ],
  (
    currentClinic,
    currentPractice,
    hasRegions,
    currentRegion,
    clinicsFilterIds,
    currentUser,
    isReady,
    currentRegionClinicsIds,
    userClinicsIds
  ) => ({
    currentClinic,
    currentPractice,
    hasRegions,
    currentRegion,
    clinicsFilterIds,
    currentUser,
    isReady,
    currentRegionClinicsIds,
    userClinicsIds
  })
);

export function StaffFilterProvider({
  children,
  disableStorage
}: StaffFilterProviderProps) {
  const dispatch = useAppDispatch();
  const {
    defaultStaff: defaultValues,
    updateStorage,
    contextKey
  } = useStorage(disableStorage);

  // Track when storage key changes (which happens on region/clinic switches)
  const prevDefaultValuesRef = useRef(defaultValues);
  const prevContextKey = useRef(contextKey);
  const justClearedStaffRef = useRef(false);
  // Tracks when clinic defaults arrive/change and need to be applied (fixes race condition)
  const pendingDefaultsApplyRef = useRef(false);
  const prevDefaultStaffIdsRef = useRef<number[] | null>(null);

  const showAudiologistsOnlyStorage = useMemo(
    () => setupLocalStorageAPI("showAudiologistsOnly"),
    []
  );

  const {
    currentClinic,
    currentPractice,
    hasRegions,
    currentRegion,
    clinicsFilterIds,
    currentUser,
    isReady,
    currentRegionClinicsIds,
    userClinicsIds
  } = useAppSelector(selectStaffFilterData);

  const dateRange = useAppSelector(selectDateRangeValues);

  const flags = useFlags();
  const schedulingV2FeatureEnabled = flags[SCHEDULING_V2_FEATURE];
  const isStaffScheduleEnabled = flags[CLINIC_STAFF_SCHEDULE];

  // Fetch clinic default staff when flag is enabled and a single clinic context is available.
  // Priority: explicit single-clinic filter > currentClinic fallback (when no filter active).
  // Multi-clinic filter = no defaults (ambiguous which clinic's schedule to use).
  const singleClinicId = isStaffScheduleEnabled
    ? clinicsFilterIds.length === 1
      ? clinicsFilterIds[0]
      : clinicsFilterIds.length === 0 && currentClinic?.id
        ? currentClinic.id
        : undefined
    : undefined;
  const { currentData: defaultStaffData } = useGetDefaultStaffQuery(
    singleClinicId && dateRange.selectedDate
      ? { clinicId: singleClinicId, date: dateRange.selectedDate }
      : skipToken
  );
  const rawDefaultStaffIds = defaultStaffData?.default_staff_ids ?? null;
  // Stabilize array reference — RTK Query returns a new array on each response even if data is identical.
  // Without this, selectedStaffMembers useMemo recomputes on every query response.
  const prevStaffIdsRef = useRef<number[] | null>(null);
  if (!isEqual(rawDefaultStaffIds, prevStaffIdsRef.current)) {
    prevStaffIdsRef.current = rawDefaultStaffIds;
  }
  const defaultStaffIds = prevStaffIdsRef.current;
  // null = not configured, [] = configured with no staff, [ids] = configured
  const clinicDefaultsActive =
    isStaffScheduleEnabled && defaultStaffIds !== null;

  const selectedDateStr = dateRange.selectedDate;
  const currentViewName = dateRange.currentViewName;
  const currentDow = useMemo(() => {
    if (!selectedDateStr) return null;
    // JS getDay: 0=Sun, convert to ISO: 1=Mon..7=Sun
    const jsDay = new Date(selectedDateStr + "T00:00:00").getDay();
    return jsDay === 0 ? 7 : jsDay;
  }, [selectedDateStr]);

  // When clinic defaults arrive or change, flag them for application on next effect cycle.
  // This ensures defaults win the race against other selection logic on initial load.
  useEffect(() => {
    if (
      clinicDefaultsActive &&
      !isEqual(defaultStaffIds, prevDefaultStaffIdsRef.current)
    ) {
      pendingDefaultsApplyRef.current = true;
    }
    prevDefaultStaffIdsRef.current = defaultStaffIds;
  }, [clinicDefaultsActive, defaultStaffIds]);

  const { userPermissions, currentUser: staffMember } = useUserRoles();
  const viewOwnScheduleOnly = userPermissions[VIEW_OWN_SCHEDULED_ONLY];

  const [showInactiveStaff, setShowInactiveStaff] = useState(false);
  const [showAudiologistsOnly, setShowAudiologistsOnly] = useState<boolean>(
    () => showAudiologistsOnlyStorage.getData(false)
  );

  const [selectedStaff, setSelectedStaff] = useState<
    Map<number, SelectedStaffMember>
  >(new Map());
  useEffect(() => {
    // Only clear on REAL context switches (e.g., region A → region B).
    // Don't clear when going from null → valid (initial load completing).
    // This prevents wiping out the selection during initialization.
    if (
      contextKey !== prevContextKey.current &&
      prevContextKey.current !== null
    ) {
      // Staff filter: Clear selected staff on region/clinic switch
      setSelectedStaff(new Map());
      justClearedStaffRef.current = true;
    }
    prevContextKey.current = contextKey;
  }, [contextKey]);

  useEffect(() => {
    if (defaultValues !== prevDefaultValuesRef.current) {
      prevDefaultValuesRef.current = defaultValues;
    }
  }, [defaultValues, selectedStaff.size]);

  // ---------- Derived clinicIds ----------
  const clinicIds = useMemo(() => {
    if (currentUser?.show_own_clinics_only) {
      // For region-based practices, intersect userClinicsIds with currentRegionClinicsIds
      // to only include clinics the user can access within the current region.
      const baseIds =
        userClinicsIds.length > 0
          ? userClinicsIds
          : ([currentClinic?.id].filter(Boolean) as number[]);

      if (hasRegions && currentRegionClinicsIds.length > 0) {
        const regionSet = new Set(currentRegionClinicsIds);
        const intersected = baseIds.filter((id) => regionSet.has(id));
        // If intersection is empty but currentClinic is in region, use that as fallback
        if (
          intersected.length === 0 &&
          currentClinic?.id &&
          regionSet.has(currentClinic.id)
        ) {
          return [currentClinic.id];
        }
        // Return intersection (may be empty if user has no access to clinics in this region)
        return intersected;
      }
      return baseIds;
    }
    if (currentPractice?.limit_to_current_clinic) {
      return [currentClinic?.id].filter(Boolean) as number[];
    }
    if (hasRegions) {
      const canUseFilterAsRegionSubset =
        !schedulingV2FeatureEnabled &&
        clinicsFilterIds.length &&
        every(clinicsFilterIds, (v) => includes(currentRegionClinicsIds, v));

      const ids = canUseFilterAsRegionSubset
        ? clinicsFilterIds
        : currentRegionClinicsIds;
      return (ids ?? []).filter(Boolean) as number[];
    }
    if (schedulingV2FeatureEnabled && !disableStorage) {
      return [currentClinic?.id].filter(Boolean) as number[];
    }
    const ids = clinicsFilterIds.length > 0 ? clinicsFilterIds : [];
    return (ids ?? []).filter(Boolean) as number[];
  }, [
    currentUser?.show_own_clinics_only,
    userClinicsIds,
    currentRegionClinicsIds,
    clinicsFilterIds,
    hasRegions,
    schedulingV2FeatureEnabled,
    disableStorage,
    currentClinic,
    currentPractice
  ]);

  // ---------- Staff params (debounced) ----------
  const params = useMemo(
    () => ({
      clinicIds,
      is_active: showInactiveStaff ? null : true,
      groupName: showAudiologistsOnly
        ? (GROUP_AUDIOLOGISTS as GroupType)
        : undefined
    }),
    [clinicIds, showInactiveStaff, showAudiologistsOnly]
  );

  // Debounce the params to reduce re-fetch
  const { value: dParams, isDebouncing } = useDebounce(params, 500);

  // Stable key so we only fire the fetch when the *effective* params change
  const paramsKey = useMemo(() => {
    const normalized = {
      ...dParams,
      clinicIds: Array.isArray(dParams.clinicIds)
        ? [...dParams.clinicIds].sort((a, b) => a - b)
        : dParams.clinicIds
    };
    return JSON.stringify(normalized);
  }, [dParams]);

  const readyToFetch = useMemo(() => {
    // Ready if: single-clinic org => currentClinic; region org => region & region clinic list
    return Boolean(
      currentClinic && hasRegions
        ? currentRegion && currentRegionClinicsIds.length
        : currentClinic
    );
  }, [
    currentClinic,
    hasRegions,
    currentRegion,
    currentRegionClinicsIds.length
  ]);

  const canFetch = readyToFetch && !isDebouncing;

  const [getStaff, { data: staff = [], isFetching, isUninitialized }] =
    useLazyGetStaffQuery();

  const isLoading = isUninitialized || isFetching;

  // Prevent multiple API calls: only call when paramsKey changes & we're allowed to fetch.
  const lastFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!canFetch) return;

    // Wait for currentUser to load before making clinic restriction decisions.
    // Without this, we might fetch with region clinic IDs before knowing the user has show_own_clinics_only.
    if (!currentUser) return;

    // Safety check: Never fetch with empty clinicIds for practices with access restrictions.
    // This prevents the backend from returning ALL staff when no clinic filter is provided.
    // Also ensure that when user restrictions apply, the correct clinic data is loaded.
    if (!dParams.clinicIds || dParams.clinicIds.length === 0) {
      if (
        hasRegions ||
        currentUser?.show_own_clinics_only ||
        currentPractice?.limit_to_current_clinic
      ) {
        return;
      }
    }

    // If user has show_own_clinics_only, wait until userClinicsIds or currentClinic is populated
    // to prevent fetching with region clinic IDs before user restrictions are applied.
    if (
      currentUser?.show_own_clinics_only &&
      userClinicsIds.length === 0 &&
      !currentClinic?.id
    ) {
      return;
    }

    // If practice has limit_to_current_clinic, wait until currentClinic is populated.
    if (currentPractice?.limit_to_current_clinic && !currentClinic?.id) {
      return;
    }

    if (lastFetchKeyRef.current !== paramsKey) {
      lastFetchKeyRef.current = paramsKey;
      // Fire exactly once per effective key
      getStaff(dParams);
    }
  }, [
    canFetch,
    paramsKey,
    getStaff,
    dParams,
    currentUser?.show_own_clinics_only,
    currentPractice?.limit_to_current_clinic,
    hasRegions,
    userClinicsIds,
    currentClinic?.id,
    currentUser
  ]);

  // ---------- Storage integration ----------
  const handleChangeStaff = useCallback(
    (value: Map<number, SelectedStaffMember>) => {
      setSelectedStaff(value);
      const pureValues = Array.from(value.values()).map(({ id, checked }) => ({
        id,
        checked
      }));
      updateStorage("staff", pureValues);
    },
    [updateStorage]
  );

  // ---------- Defaults from storage ----------
  const defaultStaffMap = useMemo(() => {
    if (!defaultValues?.length)
      return new Map<number, { id: number; checked: boolean }>();
    return new Map(
      defaultValues.map((v: { id: number; checked: boolean }) => [v.id, v])
    );
  }, [defaultValues]);

  const staffMap = useMemo(
    () => new Map(staff.map((m) => [m.id, m] as const)),
    [staff]
  );

  const includeAllStaff = useMemo(
    () => currentUser.scheduler_select_all_staff && schedulingV2FeatureEnabled,
    [currentUser.scheduler_select_all_staff, schedulingV2FeatureEnabled]
  );

  const defaultStaff = useMemo(() => {
    if (!currentUser.scheduler_select_all_staff && schedulingV2FeatureEnabled) {
      return [] as StaffMember[];
    }
    if (!defaultValues?.length) return [] as StaffMember[];

    const result: StaffMember[] = [];
    for (const v of defaultValues) {
      const m = staffMap.get(v.id);
      if (m) result.push(m);
    }
    return result;
  }, [
    currentUser.scheduler_select_all_staff,
    schedulingV2FeatureEnabled,
    defaultValues,
    staffMap
  ]);

  // ---------- Build initial selection (from defaults + roles) ----------
  const defaultSelectedStaff = useMemo(() => {
    const map = new Map<number, SelectedStaffMember>();
    defaultStaff.forEach((m, index) => {
      const def = defaultStaffMap.get(m.id);
      const checked =
        (def as { id: number; checked: boolean } | undefined)?.checked ?? false;
      const colors =
        currentUser.id === m.id
          ? getStaffColor(m.color || blue[400])
          : getStaffColor(m.color || index + 1);

      map.set(m.id, {
        ...m,
        checked,
        ...colors
      } as SelectedStaffMember);
    });
    return map;
  }, [defaultStaff, defaultStaffMap, currentUser.id]);

  const selectedStaffMembers = useMemo(() => {
    // When context changes (region/clinic switch), return empty until:
    // 1. New staff has loaded (isLoading = false), AND
    // 2. We have valid defaultValues for the new context (not undefined)
    // This prevents using stale staff array from old region/clinic.
    if (justClearedStaffRef.current) {
      // Only proceed if we have fresh data for the new context
      if (
        isLoading ||
        isDebouncing ||
        defaultValues === undefined ||
        lastFetchKeyRef.current !== paramsKey
      ) {
        return new Map<number, SelectedStaffMember>();
      }
    }

    // Check localStorage directly, not the filtered result (defaultSelectedStaff).
    // defaultSelectedStaff depends on staffMap (API response), so it's empty while loading
    // even when localStorage has saved preferences. This was causing incorrect
    // "first time visit" detection and auto-populating all staff on refresh.
    const isFirstTimeVisit = !defaultValues || defaultValues.length === 0;
    // Don't auto-populate staff until we can read from localStorage.
    // contextKey being null means auth hasn't loaded yet, not "first time visit".
    // This prevents showing ALL staff during the loading transition state.
    // CRITICAL: defaultValues is computed synchronously when contextKey changes,
    // but there's a render gap where contextKey is valid but defaultValues hasn't
    // been computed yet. We must wait for BOTH to be ready.
    const storageIsReady = contextKey !== null && defaultValues !== undefined;
    // Only apply isFirstTimeVisit when clinic filtering is correctly applied.
    // If show_own_clinics_only or limit_to_current_clinic is enabled, filtering is safe.
    // Otherwise, for region-based practices, only safe if currentRegionClinicsIds is populated
    // to prevent showing all staff across all regions.
    const hasValidShowOwnClinics =
      currentUser?.show_own_clinics_only &&
      (userClinicsIds.length > 0 || currentClinic?.id);
    const hasValidLimitToCurrentClinic =
      currentPractice?.limit_to_current_clinic && currentClinic?.id;
    const hasUserClinicRestriction =
      hasValidShowOwnClinics || hasValidLimitToCurrentClinic;
    const isRegionFilteredCorrectly =
      !hasRegions || (hasRegions && currentRegionClinicsIds.length > 0);
    const isFilteredCorrectly =
      hasUserClinicRestriction || isRegionFilteredCorrectly;
    // Clinic daily defaults apply ONLY when no localStorage preferences exist (first visit).
    // If user has saved staff preferences, respect those over admin defaults.
    // The "Reset to Defaults" button handles the explicit reset case directly.
    if (clinicDefaultsActive && isFirstTimeVisit && storageIsReady) {
      const defaultSet = new Set(defaultStaffIds);
      const newSelection = new Map<number, SelectedStaffMember>();
      staff.forEach((staffMember, index) => {
        if (defaultSet.has(staffMember.id)) {
          const colors = getStaffColor(
            staffMember.color ||
              (staffMember.id === currentUser.id ? blue[400] : index + 1)
          );
          newSelection.set(staffMember.id, {
            ...staffMember,
            checked: true,
            ...colors
          } as SelectedStaffMember);
        }
      });
      // Current user is always present — checked based on whether they're in the defaults
      const currentUserStaff = staffMap.get(currentUser.id);
      if (currentUserStaff && !newSelection.has(currentUser.id)) {
        newSelection.set(currentUser.id, {
          ...currentUserStaff,
          checked: false,
          ...getStaffColor(currentUser.color || blue[400])
        } as SelectedStaffMember);
      }
      return newSelection;
    }

    const newSelection = new Map<number, SelectedStaffMember>();

    staff.forEach((staffMember, index) => {
      const isAudiologist = staffMember.groups.some(
        ({ name }) => name === GROUP_AUDIOLOGISTS
      );

      const shouldBeCheckedByDefault =
        !disableStorage && (!schedulingV2FeatureEnabled || isAudiologist);

      const shouldInclude =
        disableStorage ||
        (schedulingV2FeatureEnabled && isAudiologist) ||
        (!schedulingV2FeatureEnabled &&
          (includeAllStaff ||
            defaultSelectedStaff.has(staffMember.id) ||
            (isFirstTimeVisit &&
              isFilteredCorrectly &&
              storageIsReady &&
              currentUser.scheduler_select_all_staff)) &&
          staffMember.id !== currentUser.id);

      if (shouldInclude) {
        // Priority 1: If user had already toggled this member in current session, keep their "checked"
        const current = selectedStaff.get(staffMember.id);
        if (current) {
          newSelection.set(staffMember.id, {
            ...staffMember,
            checked: current.checked,
            color: current.color,
            backgroundColor: current.backgroundColor,
            hoverBackgroundColor: current.hoverBackgroundColor,
            focusBackgroundColor: current.focusBackgroundColor
          });
        } else {
          // Priority 2: Check localStorage (defaultValues) for user's explicit selections
          // This preserves user selections even when includeAllStaff is true
          const storedDefault = defaultStaffMap.get(staffMember.id);
          if (storedDefault !== undefined) {
            // User has explicitly set this staff member's checked state in localStorage
            const existing = defaultSelectedStaff.get(staffMember.id);
            if (existing) {
              // Use the full defaultSelectedStaff entry (includes colors)
              newSelection.set(staffMember.id, existing);
            } else {
              // Staff member exists in storage but not in defaultSelectedStaff (maybe filtered out)
              // Use the stored checked state
              const colors = getStaffColor(
                staffMember.color || defaultStaff.length + index + 1
              );
              newSelection.set(staffMember.id, {
                ...staffMember,
                checked: Boolean(storedDefault.checked),
                ...colors
              } as SelectedStaffMember);
            }
          } else {
            // Priority 3: Fall back to defaults for brand-new staff (not in localStorage)
            const existing = defaultSelectedStaff.get(staffMember.id);
            if (existing) {
              newSelection.set(staffMember.id, existing);
            } else {
              // When includeAllStaff is true, we include all staff for visibility,
              // but default to unchecked unless explicitly set by role-based logic
              // This prevents auto-checking all staff when includeAllStaff is enabled
              let checked = shouldBeCheckedByDefault;
              const colors = getStaffColor(
                staffMember.color || defaultStaff.length + index + 1
              );

              newSelection.set(staffMember.id, {
                ...staffMember,
                checked,
                ...colors
              } as SelectedStaffMember);
            }
          }
        }
      }
    });

    const currentUserSelection = staffMap.get(currentUser.id);
    if (currentUserSelection) {
      const existing = defaultSelectedStaff.get(currentUser.id);
      newSelection.set(
        currentUser.id,
        existing || {
          ...currentUserSelection,
          checked: true,
          ...getStaffColor(currentUser.color || blue[400])
        }
      );
    }

    return newSelection;
  }, [
    defaultValues,
    contextKey,
    currentUser?.show_own_clinics_only,
    currentUser.id,
    currentUser.scheduler_select_all_staff,
    currentUser.color,
    userClinicsIds.length,
    currentClinic?.id,
    currentPractice?.limit_to_current_clinic,
    hasRegions,
    currentRegionClinicsIds.length,
    staff,
    staffMap,
    isLoading,
    isDebouncing,
    paramsKey,
    schedulingV2FeatureEnabled,
    clinicDefaultsActive,
    defaultStaffIds,
    includeAllStaff,
    defaultSelectedStaff,
    selectedStaff,
    defaultStaffMap,
    defaultStaff.length,
    disableStorage
  ]);

  const previousSelectedStaffRef = useRef<Map<number, SelectedStaffMember>>(
    new Map()
  );

  const mapsAreEqual = useCallback(
    (
      map1: Map<number, SelectedStaffMember>,
      map2: Map<number, SelectedStaffMember>
    ) => {
      if (map1.size !== map2.size) return false;
      for (const [key, value] of map1) {
        const otherValue = map2.get(key);
        if (!otherValue || !isEqual(value, otherValue)) {
          return false;
        }
      }
      return true;
    },
    []
  );

  // Avoid repeated "ready" dispatches across benign re-renders
  const readyDispatchedRef = useRef(false);
  useEffect(() => {
    if (isLoading || isDebouncing) return;

    // Only update selected staff if it's actually different
    const changed = !mapsAreEqual(
      selectedStaffMembers,
      previousSelectedStaffRef.current
    );
    if (changed) {
      previousSelectedStaffRef.current = new Map(selectedStaffMembers);

      // Overwrite selectedStaff on initial load (when empty), after clearing due to context change,
      // or when clinic defaults just arrived (prevents race where other logic populated first).
      if (
        selectedStaff.size === 0 ||
        justClearedStaffRef.current ||
        pendingDefaultsApplyRef.current
      ) {
        setSelectedStaff(selectedStaffMembers);
        // CRITICAL: Never persist immediately after a context switch (justClearedStaffRef = true).
        // The staff array might be stale from the old region/clinic.
        // Wait for the next render when justClearedStaffRef = false and we have fresh staff.
        if (!justClearedStaffRef.current) {
          // Only persist on initial load, not on filter changes
          // When includeAllStaff is true, we include all staff in selectedStaffMembers for visibility.
          // We should persist all visible staff to remember their checked/unchecked states.
          // When includeAllStaff is false, only persist checked staff or staff previously in storage.

          const merged = new Map<number, { id: number; checked: boolean }>();

          // First, preserve existing storage values (user's explicit selections)
          if (defaultValues?.length) {
            for (const v of defaultValues) {
              merged.set(v.id, { id: v.id, checked: v.checked });
            }
          }

          // Then, update with current selectedStaffMembers
          if (includeAllStaff) {
            // When includeAllStaff is true, persist all visible staff to remember their states
            for (const { id, checked } of selectedStaffMembers.values()) {
              merged.set(id, { id, checked });
            }
          } else {
            // When includeAllStaff is false, only persist checked staff or staff previously in storage
            for (const { id, checked } of selectedStaffMembers.values()) {
              if (checked || merged.has(id)) {
                merged.set(id, { id, checked });
              }
            }
          }
          updateStorage("staff", Array.from(merged.values()));
        }

        if (selectedStaffMembers.size > 0) {
          justClearedStaffRef.current = false;
        }
        pendingDefaultsApplyRef.current = false;
      }
    }

    // Dispatch ready once per mount (or after the very first apply)
    if (!readyDispatchedRef.current) {
      dispatch(staffFilterReady());
      readyDispatchedRef.current = true;
    }
  }, [
    isLoading,
    isDebouncing,
    selectedStaffMembers,
    defaultValues,
    updateStorage,
    dispatch,
    mapsAreEqual,
    selectedStaff.size,
    includeAllStaff
  ]);

  // ---------- Toggles (save only on change) ----------
  const updateShowAudiologistsOnly = useCallback(
    (value: boolean) => {
      if (value !== showAudiologistsOnly) {
        setShowAudiologistsOnly(value);
        showAudiologistsOnlyStorage.updateData(value);
      }
    },
    [showAudiologistsOnlyStorage, showAudiologistsOnly]
  );

  const updateShowInactiveStaff = useCallback(
    (value: boolean) => {
      if (value !== showInactiveStaff) setShowInactiveStaff(value);
    },
    [showInactiveStaff]
  );

  // Defensive filter: Remove stale staff IDs that are no longer in staffMap.
  // This is a last-line defense against corrupted localStorage or timing bugs.
  // If staff API returns a new set (e.g., after region switch), stale IDs get filtered out.
  const validSelectedStaff = useMemo(() => {
    // During loading or with empty staff, keep current selection as-is
    if (staffMap.size === 0 || isLoading || isDebouncing) return selectedStaff;

    // Quick check: do we have any stale IDs?
    let hasStale = false;
    for (const id of selectedStaff.keys()) {
      if (!staffMap.has(id)) {
        hasStale = true;
        break;
      }
    }

    // If no stale IDs, return original map (avoids unnecessary re-render)
    if (!hasStale) return selectedStaff;

    // Filter out stale staff IDs
    const filtered = new Map<number, SelectedStaffMember>();
    for (const [id, member] of selectedStaff) {
      if (staffMap.has(id)) {
        filtered.set(id, member);
      }
    }
    return filtered;
  }, [selectedStaff, staffMap, isLoading, isDebouncing]);

  // ---------- Outgoing filtered values (debounced) ----------
  const filteredStaffValues = useMemo(() => {
    if (!isReady || isLoading || isDebouncing) return [];

    if (viewOwnScheduleOnly) return staffMember ? [staffMember] : [];

    const result: Omit<SelectedStaffMember, "checked">[] = [];
    for (const m of validSelectedStaff.values()) {
      if (m.checked) {
        // strip "checked" before dispatching
        const { checked, ...rest } = m;
        result.push(rest);
      }
    }
    return result;
  }, [
    isReady,
    isLoading,
    isDebouncing,
    viewOwnScheduleOnly,
    staffMember,
    validSelectedStaff
  ]);

  const previousFilteredRef = useRef(filteredStaffValues);
  const { value: debouncedFiltered } = useDebounce(filteredStaffValues, 100);

  useEffect(() => {
    if (!isReady) return;
    if (!isEqual(debouncedFiltered, previousFilteredRef.current)) {
      previousFilteredRef.current = debouncedFiltered;
      dispatch(applyStaffFilter(debouncedFiltered));
    }
  }, [debouncedFiltered, dispatch, isReady]);

  const hasClinicDefaults = Boolean(
    isStaffScheduleEnabled &&
      defaultStaffIds !== null &&
      currentViewName === "Day"
  );

  // Derive the clinic name for the "Reset to Defaults (Clinic Name)" label
  const clinicDefaultsName = hasClinicDefaults
    ? currentClinic?.name || null
    : null;

  const clinicDefaultsTooltip = useMemo(() => {
    if (!hasClinicDefaults || !defaultStaffIds) return null;
    const dayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const dayLabel = currentDow ? dayLabels[currentDow - 1] : "";
    const staffNames = defaultStaffIds
      .map((id) => {
        const member = staffMap.get(id);
        return member ? `${member.first_name} ${member.last_name}` : null;
      })
      .filter(Boolean);
    const clinic = currentClinic?.name || "";
    const lines = [`${dayLabel} — ${clinic}`];
    if (staffNames.length > 0) {
      lines.push(...staffNames.map((name) => `• ${name}`));
    } else {
      lines.push("No default staff configured");
    }
    return lines.join("\n");
  }, [
    hasClinicDefaults,
    defaultStaffIds,
    currentDow,
    staffMap,
    currentClinic?.name
  ]);

  const resetToDefaults = useCallback(() => {
    if (!defaultStaffIds) return;
    // Build the correct selection directly instead of clearing and waiting for
    // the useMemo cascade (which gets blocked by the justClearedStaffRef guard
    // during debounce, causing an empty flash).
    const defaultSet = new Set(defaultStaffIds);
    const newSelection = new Map<number, SelectedStaffMember>();
    staff.forEach((staffMember, index) => {
      if (defaultSet.has(staffMember.id)) {
        const colors = getStaffColor(
          staffMember.color ||
            (staffMember.id === currentUser.id ? blue[400] : index + 1)
        );
        newSelection.set(staffMember.id, {
          ...staffMember,
          checked: true,
          ...colors
        } as SelectedStaffMember);
      }
    });
    // Always include current user — checked based on whether they're in defaults
    const currentUserStaff = staffMap.get(currentUser.id);
    if (currentUserStaff && !newSelection.has(currentUser.id)) {
      newSelection.set(currentUser.id, {
        ...currentUserStaff,
        checked: defaultSet.has(currentUser.id),
        ...getStaffColor(currentUser.color || blue[400])
      } as SelectedStaffMember);
    }
    setSelectedStaff(newSelection);
    // Persist to localStorage so refresh preserves the reset state
    const entries = Array.from(newSelection.values()).map(
      ({ id, checked }) => ({
        id,
        checked
      })
    );
    updateStorage("staff", entries);
  }, [defaultStaffIds, staff, staffMap, currentUser, updateStorage]);

  const value = useMemo(() => {
    return {
      staff,
      selectedStaff: validSelectedStaff,
      handleChangeStaff,
      showInactiveStaff,
      setShowInactiveStaff: updateShowInactiveStaff,
      showAudiologistsOnly,
      setShowAudiologistsOnly: updateShowAudiologistsOnly,
      hasClinicDefaults,
      clinicDefaultsName,
      clinicDefaultsTooltip,
      resetToDefaults
    };
  }, [
    handleChangeStaff,
    validSelectedStaff,
    showAudiologistsOnly,
    showInactiveStaff,
    hasClinicDefaults,
    clinicDefaultsName,
    clinicDefaultsTooltip,
    resetToDefaults,
    staff,
    updateShowAudiologistsOnly,
    updateShowInactiveStaff
  ]);

  return (
    <StaffFilterContext.Provider value={value}>
      {children}
    </StaffFilterContext.Provider>
  );
}

export default function useStaff() {
  const context = useContext(StaffFilterContext);
  if (!context) {
    throw new Error("useStaff must be used within a StaffFilterProvider");
  }
  return context;
}
